import React, { useState, useEffect } from 'react';
import { Clock, Calendar, BarChart3, AlertTriangle, Download } from 'lucide-react';
import { Button } from '../common/Button';
import { InterviewHistory } from '../common/InterviewHistory';
import { Link, useNavigate } from 'react-router-dom';
import { InterviewFeedbackModal } from '../interviews/InterviewFeedbackModal';
import getMockFeedback from '../../mock/interviewFeedback';
import subscriptionService, { UserSubscription, CancelSubscriptionRequest } from '../../services/subscriptionService';
import { useToast } from '../common/Toast';
import Modal from '../common/Modal';
import interviewService, { UserInterview } from '../../services/interviewService';
import ScheduleInterviewModal from '../interviews/ScheduleInterviewModal';
import { InterviewFeedback } from '../../types/interviews';
import { CancellationReasonModal } from './CancellationReasonModal';
import { useWeekendDetection } from '../../hooks/useWeekendDetection';

// Define DateFilter interface
interface DateFilter {
  value: string;
  label: string;
  fromDate?: string;
  toDate?: string;
}

interface SubscriptionUsageProps {
  userData: {
    name: string;
    profileImage?: string;
    subscriptionPlan?: string;
    subscriptionExpiry?: string;
    remainingMinutes: number;
    activePlan: 'free' | 'monthly' | 'yearly' | 'quarterly';
    isUnlimited?: boolean;
  };
  dateFilters?: DateFilter[];
  selectedDateFilter?: string;
  onDateFilterChange?: (filter: string) => void;
  interviewHistoryData?: Array<UserInterview>;
  isLoadingInterviewHistory?: boolean;
  interviewHistoryError?: string | null;
  upcomingInterviews?: Array<UserInterview>;
}


export const SubscriptionUsage: React.FC<SubscriptionUsageProps> = ({
  userData,
  dateFilters,
  selectedDateFilter: externalSelectedDateFilter,
  onDateFilterChange: externalOnDateFilterChange,
  interviewHistoryData = [],
  isLoadingInterviewHistory = false,
  interviewHistoryError = null,
  upcomingInterviews = []
}) => {
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>(externalSelectedDateFilter || 'this-month');
  const [selectedInterview, setSelectedInterview] = useState<UserInterview | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCancellationReasonModal, setShowCancellationReasonModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<UserSubscription | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [interviewHistory, setInterviewHistory] = useState<Array<UserInterview>>(interviewHistoryData);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { isWeekend, timeLeft } = useWeekendDetection();
  // Helper function to get date range for current month
  function getDateRangeForMonth(date: Date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0, 23, 59, 59, 999);

    return {
      fromDate: firstDay.toISOString(),
      toDate: lastDay.toISOString()
    };
  }

  // If no date filters are provided, use a default filter
  const defaultDateFilter = {
    value: 'this-month',
    label: 'Current Month',
    fromDate: getDateRangeForMonth(new Date()).fromDate,
    toDate: getDateRangeForMonth(new Date()).toDate
  };

  // Use provided date filters or a minimal default
  const finalDateFilters = dateFilters?.length ? dateFilters : [defaultDateFilter];

  // Find the current date filter
  const getCurrentDateFilter = (): DateFilter => {
    const selectedFilter = finalDateFilters.find(filter => filter.value === selectedDateFilter);

    if (!selectedFilter) {
      console.warn(`SubscriptionUsage: Couldn't find date filter with value: ${selectedDateFilter}. Using first filter as fallback.`);
      return finalDateFilters[0];
    }

    return selectedFilter;
  };

  // Fetch current subscription data to get accurate details
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        setIsLoadingSubscription(true);
        const subscription = await subscriptionService.getUserSubscription(true);
        console.log('SubscriptionUsage: Fetched subscription data:', subscription);
        setSubscriptionData(subscription);
      } catch (error) {
        console.error('SubscriptionUsage: Error fetching subscription data:', error);
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    fetchSubscriptionData();
  }, []);

  // Update interviewHistory state when props change
  useEffect(() => {
    if (interviewHistoryData && interviewHistoryData.length > 0) {
      // Remove out the interviews where series is present and status is not_started
      const filteredHistory = interviewHistoryData.filter((interview) => {
        if (interview.series) {
          return interview.status !== 'not_started';
        }
        return true;
      });

      setInterviewHistory(filteredHistory);
    }
  }, [interviewHistoryData]);

  // Handle date filter change
  const handleDateFilterChange = (filterValue: string) => {
    console.log(`SubscriptionUsage: Date filter changed to ${filterValue}`);

    // Update local state
    setSelectedDateFilter(filterValue);

    // Call external handler if provided
    if (externalOnDateFilterChange) {
      externalOnDateFilterChange(filterValue);
    }
  };

  // Calculate usage statistics based on subscription data or userData
  const getTotalMinutes = () => {
    if (subscriptionData && subscriptionData.plan && subscriptionData.isActive) {
      return subscriptionData.plan.interviewMinutes;
    }

    // Fallback to userData if subscription data is not available
    return userData.activePlan === 'free' ? 15 :
      userData.activePlan === 'monthly' ? 120 : 1540;
  };

  const totalMinutes = getTotalMinutes();
  const remainingMinutes = subscriptionData?.minutesLeft || userData.remainingMinutes;
  const usedMinutes = totalMinutes - remainingMinutes;
  const percentageUsed = Math.round((usedMinutes / totalMinutes) * 100);

  // Determine if user has unlimited minutes (either from subscription or weekend)
  const hasUnlimitedMinutes = userData.isUnlimited || isWeekend;

  // Calculate progress on a non-linear scale with thresholds
  const getScaledProgressPercentage = (minutesLeft: number, isUnlimited: boolean): number => {
    // Define thresholds for the non-linear scale
    const thresholds = [0, 30, 60, 120, 240, 480, 600, Infinity];
    if (isUnlimited || hasUnlimitedMinutes) {
      return 100;
    }

    // Find which threshold segment the minutes fall into
    let segmentIndex = 0;
    for (let i = 0; i < thresholds.length - 1; i++) {
      if (minutesLeft >= thresholds[i] && minutesLeft < thresholds[i + 1]) {
        segmentIndex = i;
        break;
      }
    }

    // Calculate the weight of each segment in the overall progress (total 100%)
    const segmentWeight = 100 / (thresholds.length - 1);

    // Calculate progress within the current segment
    const segmentStart = thresholds[segmentIndex];
    const segmentEnd = thresholds[segmentIndex + 1] === Infinity ?
      thresholds[segmentIndex] * 2 : thresholds[segmentIndex + 1];

    const segmentProgress = (minutesLeft - segmentStart) / (segmentEnd - segmentStart);

    // Calculate the final progress percentage
    // This is the base percentage of completed segments + progress through current segment
    const progressPercentage = (segmentIndex * segmentWeight) + (segmentProgress * segmentWeight);

    // Return the progress percentage capped at 100%
    return Math.min(Math.max(progressPercentage, 0), 100);
  };

  const getProgressColorClass = (minutesLeft: number, isUnlimited: boolean): string => {
    if (isUnlimited || hasUnlimitedMinutes) {
      return hasUnlimitedMinutes && isWeekend ? 'bg-gradient-to-r from-secondary-light via-primary-light to-secondary-dark' : 'bg-blue-600';
    }
    if (minutesLeft <= 30) return 'bg-red-500';
    if (minutesLeft <= 120) return 'bg-amber-500';
    if (minutesLeft <= 480) return 'bg-green-500';
    return 'bg-blue-600';
  };

  const scaledProgressPercentage = getScaledProgressPercentage(remainingMinutes, hasUnlimitedMinutes);
  const progressColorClass = getProgressColorClass(remainingMinutes, hasUnlimitedMinutes);

  // Format date for display
  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';

    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('SubscriptionUsage: Error formatting date:', error);
      return 'N/A';
    }
  };

  // Get start date from subscription data
  const getStartDate = (): string => {
    if (subscriptionData && subscriptionData.startDate) {
      return formatDate(subscriptionData.startDate);
    }
    return 'April 1, 2023';
  };

  // Get end/renewal date from subscription data
  const getEndDate = (): string => {
    console.log('SubscriptionUsage: subscriptionData', subscriptionData);
    if (subscriptionData && subscriptionData.endDate) {
      return formatDate(subscriptionData.endDate);
    }
    // If is recurring, return the end date + 1 day
    if (subscriptionData?.isRecurring) {
      const endDate = new Date(subscriptionData.endDate);
      endDate.setDate(endDate.getDate() + 1);
      return formatDate(endDate.toISOString());
    }
    return 'N/A';
  };

  // Get plan name from subscription data
  const getPlanName = (): string => {
    if (subscriptionData && subscriptionData.plan && subscriptionData.isActive) {
      return subscriptionData.plan.name;
    }
    return userData.subscriptionPlan || 'Free Plan';
  };

  // Handle cancellation of subscription
  const handleCancelSubscription = async () => {
    // Close the initial cancel modal and show the reason modal
    setShowCancelModal(false);
    setShowCancellationReasonModal(true);
  };

  // Handle cancellation with reasons
  const handleCancellationWithReason = async (request: CancelSubscriptionRequest) => {
    try {
      setIsCancelling(true);

      // Call cancelSubscription with the cancellation reasons
      await subscriptionService.cancelSubscription(request);

      showToast('Your subscription has been cancelled successfully. Thank you for your feedback!', 'success');

      // Close the reason modal
      setShowCancellationReasonModal(false);

      // Reload the page to refresh subscription state
      window.location.reload();
    } catch (error) {
      console.error('SubscriptionUsage: Error cancelling subscription with reason:', error);
      showToast('There was an error cancelling your subscription. Please try again later.', 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  // Get usage statistics for interviews
  const getUsageStats = () => {
    // Get all pending interviews by combining filtered interviews from interviewHistory
    // and the dedicated upcomingInterviews (which are all pending)
    // Only include 'not_started' interviews if they are standalone (no series)
    const pendingFromHistory = interviewHistory.filter(i =>
      i.status === 'scheduled' || // Always include scheduled interviews
      (i.series && i.status !== 'not_started') || // Series interviews: exclude not_started
      (!i.series && i.status === 'not_started') // Standalone interviews: include not_started
    );

    // Create a Set of interview IDs from pendingFromHistory to avoid duplicates
    const pendingIds = new Set(pendingFromHistory.map(i => i._id));

    // Add non-duplicate upcoming interviews
    const additionalUpcoming = upcomingInterviews.filter(i => !pendingIds.has(i._id));

    // Calculate total pending interviews
    const totalPending = pendingFromHistory.length + additionalUpcoming.length;

    console.log(`Usage statistics: ${pendingFromHistory.length} pending from history + ${additionalUpcoming.length} unique from upcoming = ${totalPending} total pending`);

    return {
      total: interviewHistory.length + additionalUpcoming.length,
      pending: totalPending,
      completed: interviewHistory.filter(i => i.status === 'completed').length,
      cancelled: interviewHistory.filter(i => i.status === 'cancelled' || i.status === 'expired').length
    };
  };

  // Handle view feedback button click
  const handleViewFeedback = (interviewId: string) => {
    // Find the interview in the interviewHistory array
    const interview = interviewHistory.find(i => i._id === interviewId);
    if (interview) {
      // Add the interview details to the feedback object
      if (interview.feedback) {
        interview.feedback.interviewDetails = {
          ...interview.interview,
          userInterviewId: interview._id,
          title: interview.interview?.title || '',
          company: interview.interview?.company || '',
          jobRole: interview.interview?.role || '',
          duration: interview.interview?.durationMinutes || 30,
          minutesUsed: interview.minutesUsed,
          description: interview.interview?.description || '',
          language: interview.interview?.language || 'English',
          tags: interview.interview?.tags || [],
          scheduledAt: interview.scheduledAt,
          userFeedbackRating: interview.userFeedbackRating,
          newInterview: false
        }
      } else {
        showToast('Interview feedback not generated as no interview activity was found', 'info');
      }
      setSelectedInterview(interview);
      setShowFeedbackModal(true);
    }
  };


  // Handle rejoin interview
  const handleRejoinInterview = async (interviewId: string) => {
    try {
      const interview = interviewHistory.find(interview => interview._id === interviewId) || null;
      if (!interview) {
        console.error('Interview not found');
        showToast('Interview not found', 'error');
        return;
      }
      // Navigate to interview preview page with interview details
      navigate(`/interview-preview/${interviewId}`, {
        state: {
          interviewDetails: {
            scheduledInterviewId: interview._id, // This is the userInterviewId which means user's scheduled interview id
            id: interview.interview?._id || '',
            title: interview.interview?.title || '',
            company: interview.interview?.company || '',
            companyLogo: interview.interview?.companyLogo || '',
            companyId: interview.interview?.companyId || '',
            role: interview.interview?.role || '',
            duration: interview.interview?.durationMinutes || 30,
            description: interview.interview?.description || '',
            language: interview.interview?.language || 'English',
            tags: interview.interview?.tags || [],
            status: interview.status,
            voicePack: interview.interview?.voicePack || '',
            characterGender: interview.interview?.characterGender || '',
            interviewPrompt: interview.interview?.interviewPrompt || '',
            createdAt: interview.createdAt,
            updatedAt: interview.updatedAt,
            newInterview: false,
            series: interview.series,
            isCustom: interview.isCustomInterview
          }
        }
      });
    } catch (err) {
      console.error('Error joining interview:', err);
      showToast('Failed to join interview. Please try again.', 'error');
    }
  };

  // Handle reschedule interview
  const handleRescheduleInterview = (interviewId: string) => {
    setSelectedInterview(interviewHistory.find(interview => interview._id === interviewId) || null);
    setShowRescheduleModal(true);
  };

  // Handle reschedule submit
  const handleRescheduleSubmit = async (date: Date, language?: string) => {
    if (!selectedInterview) return;

    try {
      // Call the API to reschedule the interview
      await interviewService.rescheduleInterview(
        selectedInterview._id,
        date,
        language
      );

      // Show success message
      showToast('Interview rescheduled successfully', 'success');

      // Refresh data from server to ensure consistency
      handleDateFilterChange(selectedDateFilter);
    } catch (err) {
      console.error('Error rescheduling interview:', err);
      showToast('Failed to reschedule interview. Please try again.', 'error');
    } finally {
      setShowRescheduleModal(false);
    }
  };

  // Handle closing the feedback modal
  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
  };

  // Update local state when external selectedDateFilter changes
  useEffect(() => {
    if (externalSelectedDateFilter && externalSelectedDateFilter !== selectedDateFilter) {
      setSelectedDateFilter(externalSelectedDateFilter);
    }
  }, [externalSelectedDateFilter, selectedDateFilter]);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
        {/* Minutes Usage Card */}
        <div className={`bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30 ${isWeekend ? 'relative overflow-hidden' : ''
          }`}>
          <div className="relative z-10">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-full mr-3 ${isWeekend
                  ? 'bg-secondary-light/10 dark:bg-secondary-dark/10'
                  : 'bg-primary-light/10 dark:bg-primary-dark/10'
                }`}>
                <Clock className={`w-5 h-5 ${isWeekend
                    ? 'text-secondary-light dark:text-secondary-dark'
                    : 'text-primary-light dark:text-primary-dark'
                  }`} />
              </div>
              <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
                Interview Minutes
              </h3>
            </div>

            <div className={`flex justify-between items-baseline ${hasUnlimitedMinutes ? 'mb-0' : 'mb-2'}`}>
              <div className={`text-3xl font-bold ${isWeekend
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-secondary-light via-primary-light to-secondary-dark'
                  : 'text-text-primary-light dark:text-text-primary-dark'
                }`}>
                {hasUnlimitedMinutes ? (
                  <span className={`text-4xl ${isWeekend
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-secondary-light via-primary-light to-secondary-dark'
                      : 'text-primary-light dark:text-primary-dark'
                    }`}>
                    ∞
                  </span>
                ) : remainingMinutes}
              </div>
              <div className={`flex items-center text-base text-text-secondary-light dark:text-text-secondary-dark gap-1 ${hasUnlimitedMinutes ? 'mb-2' : 'mb-0'}`}>
                <span>Minutes left</span>
                <span className="group relative inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cursor-help">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-surface-light dark:bg-surface-dark rounded-lg shadow-lg border border-border-light dark:border-border-dark text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 invisible group-hover:visible z-10">
                    <div className="font-medium mb-1 text-text-primary-light dark:text-text-primary-dark">Minute Left Scale</div>
                    <div className="grid grid-cols-2 gap-1">
                      <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500"></div> 0-30: Very Low</div>
                      <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-500"></div> 31-120: Low</div>
                      <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500"></div> 121-480: Good</div>
                      <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500"></div> 481+: Excellent</div>
                      <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-600"></div> ∞: Unlimited</div>
                      {isWeekend && (
                        <div className="flex items-center gap-1 col-span-2 mt-1 pt-1 border-t border-border-light dark:border-border-dark">
                          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-secondary-light to-secondary-dark"></div> 🔥 Weekend: Unlimited
                        </div>
                      )}
                    </div>
                  </div>
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full h-3 bg-background-light dark:bg-background-dark rounded-full overflow-hidden mb-2 relative">
              {/* Main progress bar */}
              <div
                className={`h-full ${progressColorClass} rounded-full transition-all duration-300`}
                style={{ width: `${scaledProgressPercentage}%` }}
              ></div>

              {/* Threshold markers (except for 0 and Infinity) */}
              {!hasUnlimitedMinutes && [30, 60, 120, 240, 480, 960].map((threshold, index) => {
                // Calculate position as percentage of the non-linear scale
                const position = ((index + 1) * 100) / 7; // 7 segments total
                return (
                  <div
                    key={threshold}
                    className="absolute top-0 w-px h-full bg-background-light dark:bg-background-dark opacity-70"
                    style={{ left: `${position}%` }}
                  ></div>
                );
              })}
            </div>

            <div className="flex justify-between mt-1 text-sm text-text-secondary-light dark:text-text-secondary-dark">
              <div className="font-medium">
                {hasUnlimitedMinutes ? (
                  isWeekend ? (
                    <span className="text-secondary-light dark:text-secondary-dark">
                      Weekend Unlimited - No limits, only practice
                    </span>
                  ) : (
                    'Unlimited minutes - No limits'
                  )
                ) : (
                  `${remainingMinutes} minutes left`
                )}
              </div>
              <div className="font-medium">
                {hasUnlimitedMinutes ? (
                  <span className={isWeekend ? 'text-secondary-light dark:text-secondary-dark' : ''}>
                    ∞
                  </span>
                ) : (
                  remainingMinutes <= 30 ? "Low" :
                    remainingMinutes <= 120 ? "Medium" :
                      remainingMinutes <= 480 ? "Good" : "Excellent"
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Current Billing Cycle */}
        <div className="bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
              <Calendar className="w-5 h-5 text-primary-light dark:text-primary-dark" />
            </div>
            <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
              Current Billing Cycle
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Current Plan
              </div>
              <div className={`font-medium ${userData.activePlan === 'free'
                  ? 'text-amber-500'
                  : 'text-green-500 dark:text-green-400'
                }`}>
                {getPlanName()}
              </div>
            </div>

            <div>
              <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Started On
              </div>
              <div className="text-text-primary-light dark:text-text-primary-dark">
                {getStartDate()}
              </div>
            </div>

            <div>
              <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                {subscriptionData?.isRecurring ? 'Renews On' : 'Expires On'}
              </div>
              <div className="text-text-primary-light dark:text-text-primary-dark">
                {getEndDate()}
              </div>
            </div>

            {userData.activePlan !== 'free' && subscriptionData?.isActive ? (
              <div className="flex flex-col mt-2 pt-4 space-y-3 border-t border-border-light dark:border-border-dark">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<AlertTriangle size={16} />}
                  onClick={() => setShowCancelModal(true)}
                  className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 hover:!border-red-500"
                >
                  Cancel Plan
                </Button>
              </div>
            ) : (userData.activePlan === 'free' ? (
              <div className="flex flex-col mt-2 pt-4 space-y-0 border-t border-border-light dark:border-border-dark">
                <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm mt-2">
                  No active plan. Upgrade to a paid plan.
                </p>
              </div>
            ) : (
              <div className="flex flex-col mt-2 pt-4 space-y-3 border-t border-border-light dark:border-border-dark">
                <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                  Subscription cancelled, but is active until <br /><span className="font-medium text-amber-500 dark:text-amber-400">{getEndDate()}</span>
                </p>
              </div>
            ))
            }
          </div>
        </div>

        {/* Usage Statistics */}
        <div className="bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
              <BarChart3 className="w-5 h-5 text-primary-light dark:text-primary-dark" />
            </div>
            <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
              Usage Statistics
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Interviews Completed
              </div>
              <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                {getUsageStats().completed}
              </div>
            </div>

            <div>
              <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Interviews Pending
              </div>
              <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                {getUsageStats().pending}
              </div>
            </div>

            <div>
              <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Interviews Cancelled
              </div>
              <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                {getUsageStats().cancelled}
              </div>
            </div>

            <div className="pt-4 mt-2 border-t border-border-light dark:border-border-dark">
              <Link
                to="/my-interviews?tab=analytics"
                className="text-primary-light dark:text-primary-dark hover:underline text-sm font-medium inline-flex items-center"
              >
                View detailed history and analytics
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Interview History */}
      <InterviewHistory
        interviews={interviewHistory}
        dateFilters={finalDateFilters}
        selectedDateFilter={selectedDateFilter}
        onDateFilterChange={handleDateFilterChange}
        onViewFeedback={handleViewFeedback}
        onRejoinInterview={handleRejoinInterview}
        onRescheduleInterview={handleRescheduleInterview}
        isLoading={isLoadingInterviewHistory}
        error={interviewHistoryError}
        key={`interview-history-${selectedDateFilter}`}
      />

      {/* Cancel Subscription Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
        maxWidth="450px"
      >
        <div className="p-5">
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-full bg-red-500/10 mr-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark">
              Are you sure you want to cancel your subscription? You'll still have access to your current plan until <span className="font-medium text-text-primary-light dark:text-text-primary-dark">{getEndDate()}</span>.
            </p>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(false)}
              className="w-full sm:w-auto"
              disabled={isCancelling}
            >
              Keep Subscription
            </Button>
            <Button
              variant="primary"
              onClick={handleCancelSubscription}
              className="w-full sm:w-auto !bg-red-500 hover:!bg-red-600 !border-red-500 !shadow-red-500/30"
              loading={isCancelling}
            >
              Yes, Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancellation Reason Modal */}
      <CancellationReasonModal
        isOpen={showCancellationReasonModal}
        onClose={() => setShowCancellationReasonModal(false)}
        onConfirm={handleCancellationWithReason}
        isLoading={isCancelling}
        planName={getPlanName()}
        endDate={getEndDate()}
      />

      {showRescheduleModal && selectedInterview && (
        <ScheduleInterviewModal
          isOpen={showRescheduleModal}
          onClose={() => setShowRescheduleModal(false)}
          interviewDetails={{
            id: selectedInterview?.interview?._id || '',
            title: selectedInterview?.interview?.title || '',
            description: selectedInterview?.interview?.description || '',
            company: selectedInterview?.interview?.company || '',
            jobRole: selectedInterview?.interview?.role || '',
            duration: selectedInterview?.interview?.durationMinutes || 30,
            language: selectedInterview?.interview?.language || 'English',
          }}
          initialDate={selectedInterview?.scheduledAt}
          mode="reschedule"
          onSchedule={handleRescheduleSubmit}
        />
      )}

      {/* Interview Feedback Modal */}
      {selectedInterview && showFeedbackModal && selectedInterview.feedback && (
        <InterviewFeedbackModal
          isOpen={showFeedbackModal}
          onClose={handleCloseFeedbackModal}
          feedback={selectedInterview.feedback as InterviewFeedback}
        />
      )}
    </div>
  );
}; 